/* eslint-disable @atlassian/relay/graphql-naming */
import React, { memo, useCallback } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { fg } from '@atlassian/jira-feature-gating';
import type { RenderChildRows } from '@atlassian/jira-issue-table-hierarchy/src/ui/types.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component';
import type { PopulateConnectionArgs } from '@atlassian/jira-issue-table-hierarchy/src/controllers/hierarchy/index.tsx';
import type {
	Column,
	TableInjectedCellProps,
	TopLevelIssueCreateConfig,
} from '../../../common/types.tsx';
import Rows from '../../rows/Rows.tsx';
import { RowContextProvider } from '../../rows/row-context/index.tsx';
import type { TableBody_rules$key } from './__generated__/TableBody_rules.graphql';
import type { TableBody_rankField$key } from './__generated__/TableBody_rankField.graphql';
import type { TableBody_project$key } from './__generated__/TableBody_project.graphql';
import type { TableBodyTableBody$key } from './__generated__/TableBodyTableBody.graphql';
import { RenderChildRowsComp } from './render-child-rows/RenderChildRowsComp.tsx';

export type Props = {
	columns: Column[];
	tableInjectedProps: TableInjectedCellProps;
	issueResults: TableBodyTableBody$key;
	project: TableBody_project$key | null;
	rankField: TableBody_rankField$key | null;
	isGroupingSupported?: boolean;
	resizingColumnIndex?: number;
	isConditionalFormattingEnabled: boolean;
	isDraggingRowFaded?: boolean;
	formattingRules?: TableBody_rules$key | null;
	rowInlineCreateCanShowAdditionalFields: boolean;
	topLevelIssueCreateConfig?: TopLevelIssueCreateConfig;
	initialPopulateConnectionArgs?: PopulateConnectionArgs[];
};

const TableBodyNonMemoized = ({
	issueResults,
	project,
	rankField,
	isGroupingSupported,
	isConditionalFormattingEnabled,
	isDraggingRowFaded,
	formattingRules,
	rowInlineCreateCanShowAdditionalFields,
	topLevelIssueCreateConfig,
	initialPopulateConnectionArgs,
	...tableBodyProps
}: Props) => {
	const issueResultsData = useFragment<TableBodyTableBody$key>(
		graphql`
			fragment TableBodyTableBody on JiraIssueConnection
			@argumentDefinitions(
				isInlineEditingEnabled: { type: "Boolean!" }
				isReparentingEnabled: { type: "Boolean!" }
				isTimelineEnabled: { type: "Boolean!" }
				isDensityFull: { type: "Boolean!" }
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				shouldQueryHasChildren: { type: "Boolean!" }
				isConditionalFormattingEnabled: { type: "Boolean!" }
				isLinkRelationshipFieldEnabled: { type: "Boolean!" }
				isRemoteLinkedIssuesEnabled: { type: "Boolean!" }
				isEditableSyncLoaded: { type: "Boolean!" }
			) {
				__id
				...Rows
					@arguments(
						isInlineEditingEnabled: $isInlineEditingEnabled
						isReparentingEnabled: $isReparentingEnabled
						isTimelineEnabled: $isTimelineEnabled
						isDensityFull: $isDensityFull
						projectKeys: $projectKeys
						projectKey: $projectKey
						shouldQueryHasChildren: $shouldQueryHasChildren
						isConditionalFormattingEnabled: $isConditionalFormattingEnabled
						isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
						isRemoteLinkedIssuesEnabled: $isRemoteLinkedIssuesEnabled
						isEditableSyncLoaded: $isEditableSyncLoaded
					)
			}
		`,
		issueResults,
	);

	const projectData = useFragment<TableBody_project$key>(
		graphql`
			fragment TableBody_project on JiraProject
			@argumentDefinitions(isConditionalFormattingEnabled: { type: "Boolean!" }) {
				key
				...Rows_project @arguments(isConditionalFormattingEnabled: $isConditionalFormattingEnabled)
				...RenderChildRowsComp
					@arguments(isConditionalFormattingEnabled: $isConditionalFormattingEnabled)
			}
		`,
		project,
	);

	const formattingRulesData = useFragment<TableBody_rules$key>(
		graphql`
			fragment TableBody_rules on JiraFormattingRuleConnection {
				...Rows_rules
				...RenderChildRowsComp_rules
			}
		`,
		formattingRules,
	);

	const rankFieldData = useFragment<TableBody_rankField$key>(
		graphql`
			fragment TableBody_rankField on JiraJqlFieldWithAliases {
				fieldId
				...RenderChildRowsComp_rank
			}
		`,
		rankField,
	);

	const renderChildRows: RenderChildRows = useCallback(
		(props) => (
			<RenderChildRowsComp
				project={projectData}
				projectKey={projectData?.key}
				columns={tableBodyProps.columns}
				isGroupingSupported={isGroupingSupported}
				rankField={rankFieldData ?? null}
				formattingRules={formattingRulesData}
				rowInlineCreateCanShowAdditionalFields={rowInlineCreateCanShowAdditionalFields}
				{...props}
			/>
		),
		[
			projectData,
			tableBodyProps.columns,
			isGroupingSupported,
			rankFieldData,
			formattingRulesData,
			rowInlineCreateCanShowAdditionalFields,
		],
	);

	return (
		<StyledTableBody>
			<RowContextProvider {...tableBodyProps} renderChildRows={renderChildRows}>
				<Rows
					issueResults={issueResultsData}
					parentItemId="root"
					itemsConnectionId={issueResultsData.__id}
					project={projectData}
					rankFieldId={rankFieldData?.fieldId}
					isDraggingRowFaded={isDraggingRowFaded}
					formattingRules={formattingRulesData}
					rowInlineCreateCanShowAdditionalFields={rowInlineCreateCanShowAdditionalFields}
					topLevelIssueCreateConfig={topLevelIssueCreateConfig}
					{...(fg('jsc_list_reparenting') ? { projectKey: projectData?.key } : {})}
					initialPopulateConnectionArgs={initialPopulateConnectionArgs}
				/>
			</RowContextProvider>
		</StyledTableBody>
	);
};

const TableBody = componentWithFG(
	'initialise_virtualiser_state_from_relay_store',
	memo(TableBodyNonMemoized),
	TableBodyNonMemoized,
);
export default TableBody;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledTableBody = styled.tbody({
	borderBottom: 'none',
	// Ensure position is correctly calculated when an inline edit popup is opened that overflows the table body
	overflowX: 'hidden',
});
